import {cn} from '@/utils';
import {ReactNode} from 'react';
import LoadingSpinner from '@components/LoadingSpinner';

export interface ButtonProps {
  className?: string;
  label: ReactNode;
  type?: 'button' | 'submit';
  size?: 'tiny' | 'small' | 'medium' | 'large';
  skin?: 'default' | 'primary' | 'danger' | 'secondary';
  priority?: 'primary' | 'secondary' | 'tertiary';
  disabled?: boolean;
  hasLoadingSpinner?: boolean;
  isLoading?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
}
const disabledStyles =
  'bg-neutral-100 text-neutral-500 border border-neutral-100 cursor-not-allowed';
const colorVariants: Record<string, any> = {
  primary: {
    primary: 'bg-primary text-white',
    secondary: 'bg-secondary text-primary border border-secondary',
  },
  secondary: {
    primary: 'bg-secondary text-white',
    secondary: 'bg-primary border  border-primary text-secondary',
  },
  danger: {
    primary: 'bg-red-600',
    secondary: 'bg-white border border-red-600 text-red-600',
  },
  default: {
    primary: 'bg-neutral-100',
    secondary: 'bg-white border border-slate-700 text-slate-700',
  },
  style: 'bg-neutral-100 text-neutral-500 border border-neutral-100',
  disabled: {
    primary: disabledStyles,
    secondary: disabledStyles,
    tertiary: 'bg-white border border-neutral-300 text-[#CFCFCF]',
  },
};

const sizeVariants: Record<string, string> = {
  tiny: 'h-8 text-xs rounded-md',
  small: 'py-[0.5rem] text-sm leading-5 rounded-md xl:text-xs',
  medium: 'h-md text-sm rounded-md',
  large: 'h-lg text-lg rounded-lg',
};

export default function Button({
  className,
  label,
  type = 'button',
  size = 'medium',
  skin = 'default',
  priority = 'secondary',
  disabled = false,
  onClick = () => {},
  hasLoadingSpinner,
  isLoading,
}: ButtonProps) {
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (isLoading) return;

    e.stopPropagation();
    onClick(e);
  };

  return (
    <button
      type={type}
      className={cn([
        'font-bold',
        colorVariants[disabled ? 'disabled' : skin][priority],
        sizeVariants[size],
        className,
      ])}
      onClick={handleClick}
      disabled={disabled}>
      {hasLoadingSpinner ? (
        isLoading ? (
          <div className='relative'>
            <span className={isLoading ? 'opacity-0' : ''}>{label}</span>
            <div
              className='flex items-center justify-center absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'
              onClick={e => e.stopPropagation()}>
              <LoadingSpinner />
            </div>
          </div>
        ) : (
          label
        )
      ) : (
        label
      )}
    </button>
  );
}
